import { defineStore } from 'pinia'
import { factory } from '../../../../factory-api/factory'
import {
  type BuildingBlockRequest,
  type BuildingBlockResponse,
  type ModuleFormAnswer,
  type UpdateModuleFormRequest,
  type ArticleForm,
  type BuildingBlockSimple,
  type BuildingBlockItem,
  type BuildingBlockCategory
} from '../../../../generated-api'
import type { ITab } from '../../../interfaces/ITab'
import { type IFilterItem, FilterItemType } from '../../../interfaces/IFilterItem'
import { useAppStore, useLocalizationStore, useUserStore } from '@/stores'
import router from '../../../router'
import { Capacitor } from '@capacitor/core'
import { getLocalStorageTab, setLocalStorageTab } from '../../../application/helpers/utilities'
import { httpService } from '@/services/HttpService'
import { convertPropertyNames } from '@/application/helpers'

export const useModuleStore = defineStore('module', () => {
  const appStore = useAppStore()
  const userStore = useUserStore()
  const localizationStore = useLocalizationStore()

  const currentModule = ref<BuildingBlockSimple | undefined>(undefined)
  const moduleData = ref<BuildingBlockResponse | undefined>(undefined)
  const tabs = ref<ITab[]>([
    {
      id: 1,
      name: '',
      href: '#',
      icon: 'calendar',
      current: false
    },
    { id: 2, name: '', href: '#', icon: 'bookmark', current: false }
  ])
  const activeTab = ref<ITab>(tabs.value[0])
  const filters = ref<IFilterItem[]>([])

  const widthAuto = ref<string>('mx-auto')
  const widthWideClass = ref<string>('max-w-7xl')
  const widthNarrowClass = ref<string>('max-w-3xl')
  const activeArticleAccordion = ref<string>('')

  const compactView = ref<boolean>(false)
  const automaticModuleCompletion = ref<boolean>(false)

  const favoriteAmount = ref<number>(0)
  const timeSpent = ref<number>(0)

  const init = async () => {
    await calculateFavorites()
    updateTabs()
    setFilters()

    setActiveTab(getLocalStorageTab(appStore.content?.title, tabs.value))
  }

  const initModule = async (content: BuildingBlockSimple) => {
    // Init the current module
    console.log('Init the current module')
    moduleData.value = undefined
    currentModule.value = content

    if (userStore.member.isCandidateProxyLogin === true) {
      getModuleStatus()
    } else {
      setCompleted(content.id ?? 0, 'v')
    }
  }

  const getModuleStatus = async () => {
    const moduleId = currentModule.value?.id

    // action: "v" - means: Module Viewed
    const req: BuildingBlockRequest = {
      action: 'v',
      id: moduleId
    }

    const buildingblockApi = await factory.getBuildingblockApi()
    const response: BuildingBlockResponse = await buildingblockApi.buildingBlockStatus({
      buildingBlockRequest: req
    })

    console.log('getModuleStatus', response)
    moduleData.value = response
  }

  const setFavorites = async (item: BuildingBlockItem) => {
    const req = {
      id: item.id,
      favorite: item.favorite != undefined ? !item.favorite : !moduleData.value?.isFavorite
    }
    const buildingblockApi = await factory.getBuildingblockApi()
    const response = await buildingblockApi.buildingBlockFavorite({ buildingBlockRequest: req })
    console.log('setFavorites', response)
    moduleData.value = response

    if (response.isFavorite === true) {
      favoriteAmount.value++
    } else {
      favoriteAmount.value--
    }

    updateTabs()

    return response.isFavorite
  }

  //Action V is Viewed, action C is completed. UndoCompleted do not require action
  const setCompleted = async (moduleId: number, action: string) => {
    const request = {
      id: moduleId,
      action: action,
      app: Capacitor.isNativePlatform(),
      timeSpent: 0
    }
    let response
    const buildingblockApi = await factory.getBuildingblockApi()
    if (moduleData.value?.isCompleted !== true) {
      request.timeSpent = Math.round((new Date().getTime() - timeSpent.value) / 1000)
      response = await buildingblockApi.buildingBlockCompleted({ buildingBlockRequest: request })
    } else {
      response = await buildingblockApi.buildingBlockUndoCompleted({
        buildingBlockRequest: request
      })
    }
    console.log('setCompleted module.ts', response)
    moduleData.value = response
  }

  const setFilters = () => {
    filters.value = [
      {
        id: 'categories',
        name: localizationStore.translate('building_Block_Choose_Category'),
        options: appStore.content?.allTags?.map((item: string) => {
          return {
            value: item,
            name: item
          }
        }),
        selected: [],
        hide: false,
        type: FilterItemType.Checkbox
      },
      {
        id: 'types',
        name: localizationStore.translate('building_Block_Status'),
        options: [
          { value: 'false', name: localizationStore.translate('general_Not_Completed') },
          { value: 'true', name: localizationStore.translate('general_Completed') }
        ],
        selected: [],
        hide: appStore.content.disableCompletion === true ? true : false,
        type: FilterItemType.Checkbox
      }
    ]
  }

  const getFavoritesModules = () => {
    const isFavorite = (item: BuildingBlockItem) => item.favorite

    return {
      list: appStore.content.list.filter(isFavorite),
      categories: appStore.content.categories.map((modules: BuildingBlockCategory) => ({
        ...modules,
        list: modules.list?.filter(isFavorite)
      }))
    }
  }

  const calculateFavorites = () => {
    favoriteAmount.value = appStore.content.categories.reduce(
      (count: number, modules: BuildingBlockCategory) => {
        return (
          count + (modules.list?.filter((module: BuildingBlockItem) => module.favorite).length ?? 0)
        )
      },
      getFavoritesModules().list.length
    )
  }

  const updateTabs = () => {
    tabs.value[0].name = localizationStore.translate('building_Block_All_Modules')
    tabs.value[1].name = `${localizationStore.translate('building_Block_My_Modules')} (${
      favoriteAmount.value
    })`
  }

  const setCompactView = (bool: boolean) => {
    compactView.value = bool
  }

  const setActiveTab = (tab: ITab) => {
    activeTab.value = tab
    setLocalStorageTab(tab.id, appStore.content?.title)
  }

  const saveArticleForm = async (
    article: ArticleForm,
    answer: ModuleFormAnswer
  ): Promise<boolean> => {
    // Save data for article form'
    const nodeId = article.articleContent?.nodeid ?? 1
    const moduleName = currentModule.value?.title ?? ''
    const columnCount = article.columnCount ? article.columnCount : '0'

    const req: UpdateModuleFormRequest = {
      nodeId: nodeId,
      moduleName: moduleName,
      columnCount: columnCount,
      moduleFormAnswer: answer
    }

    const moduleFormApi = await factory.getModuleFormApi()
    const response = await moduleFormApi.updateModuleForm(req)

    return response as unknown as boolean
  }

  const goToModule = (item: BuildingBlockItem) => {
    if (item.redirectLink !== '') {
      router.push({ path: item.redirectLink ?? '' })
    } else {
      appStore.getContent(item.url || '')
      router.push({ path: item.url || '' })
    }
  }

  const setActiveArticleAccordion = (id: string) => {
    activeArticleAccordion.value = id
  }

  const setTimeSpent = (time: number) => {
    timeSpent.value = time
  }

  const setAutomaticModuleCompletion = (autoComplete: boolean) => {
    automaticModuleCompletion.value = autoComplete
  }

  const updateFilterItem = (id: string, selected: string[]) => {
    filters.value = filters.value.map((filter) =>
      filter.id === id ? { ...filter, selected } : filter
    )
  }

  const getModules = async (contentUrl: string | undefined): Promise<BuildingBlockItem[]> => {
    if (contentUrl != undefined) {
      try {
        const url = `${appStore.apiUrl}/contentservice/${contentUrl}`
        const response = await httpService.get(url)

        const jsonData = await response.json()
        const convertedData = await convertPropertyNames(jsonData)

        return convertedData.list as BuildingBlockItem[]
      } catch (error) {
        console.error('Error parsing JSON:', error)
      }
    }

    return []
  }

  return {
    init,
    initModule,
    moduleData,
    setFavorites,
    setCompleted,
    tabs,
    activeTab,
    setActiveTab,
    filters,
    widthAuto,
    widthWideClass,
    widthNarrowClass,
    compactView,
    setCompactView,
    saveArticleForm,
    goToModule,
    activeArticleAccordion,
    setActiveArticleAccordion,
    updateTabs,
    getFavoritesModules,
    setTimeSpent,
    timeSpent,
    setAutomaticModuleCompletion,
    automaticModuleCompletion,
    updateFilterItem,
    getModules
  }
})
